import { useState, useRef, useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/utils/cn'

export interface TooltipProps {
  content: ReactNode
  children: ReactNode
  position?: 'top' | 'bottom' | 'left' | 'right'
  delay?: number
  className?: string
  contentClassName?: string
}

const Tooltip = ({
  content,
  children,
  position = 'top',
  delay = 300,
  className,
  contentClassName,
}: TooltipProps) => {
  const [isVisible, setIsVisible] = useState(false)
  const [coords, setCoords] = useState({ x: 0, y: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

  const showTooltip = () => {
    timeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect()
        const scrollX = window.scrollX
        const scrollY = window.scrollY

        let x = rect.left + scrollX + rect.width / 2
        let y = rect.top + scrollY

        switch (position) {
          case 'bottom':
            y = rect.bottom + scrollY + 8
            break
          case 'left':
            x = rect.left + scrollX - 8
            y = rect.top + scrollY + rect.height / 2
            break
          case 'right':
            x = rect.right + scrollX + 8
            y = rect.top + scrollY + rect.height / 2
            break
          default: // top
            y = rect.top + scrollY - 8
        }

        setCoords({ x, y })
        setIsVisible(true)
      }
    }, delay)
  }

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    setIsVisible(false)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const getTransformOrigin = () => {
    switch (position) {
      case 'bottom':
        return 'top center'
      case 'left':
        return 'right center'
      case 'right':
        return 'left center'
      default:
        return 'bottom center'
    }
  }

  const getTranslate = () => {
    switch (position) {
      case 'bottom':
        return { x: '-50%', y: '0%' }
      case 'left':
        return { x: '-100%', y: '-50%' }
      case 'right':
        return { x: '0%', y: '-50%' }
      default:
        return { x: '-50%', y: '-100%' }
    }
  }

  const translate = getTranslate()

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
        className={cn('inline-block', className)}
      >
        {children}
      </div>

      {typeof window !== 'undefined' &&
        createPortal(
          <AnimatePresence>
            {isVisible && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                style={{
                  position: 'absolute',
                  left: coords.x,
                  top: coords.y,
                  transform: `translate(${translate.x}, ${translate.y})`,
                  transformOrigin: getTransformOrigin(),
                  zIndex: 9999,
                }}
                className={cn(
                  'px-3 py-2 text-sm rounded-lg shadow-lg',
                  'bg-slate-900 dark:bg-slate-100',
                  'text-white dark:text-slate-900',
                  'max-w-xs',
                  contentClassName
                )}
              >
                {content}
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
    </>
  )
}

export default Tooltip
